<template>
  <header class="header">
    <div class="header-content">
      <router-link
        v-if="useExternalHeaderLayout"
        :to="{ name: RouteNames.LOGIN }"
        class="etconnect-mark-link"
        data-test="header-link-home"
      >
        <img
          class="etconnect-mark"
          :src="icon"
          alt="ET-Connect"
          :style="{ height: Config.isCompass ? '34px' : '26px' }"
        />
      </router-link>

      <NavigationDropdown v-else-if="!isMobile" class="header__navigation" />
      <div v-else>
        <SVGIcon
          v-if="navigationOpen"
          class="navigation-button"
          iconName="x-etc"
          alt="Navi"
          @click="setNavigationOpen(false)"
          data-test="header-link-x"
        />
        <SVGIcon
          v-else
          class="navigation-button"
          iconName="hamburger"
          alt="Navi"
          @click="setNavigationOpen(true)"
          data-test="header-link-hamburger"
        />
      </div>

      <div class="header__actions">
        <ExternalLinks v-model:dialog-open="externalDialogOpen" />
        <LanguageSelection v-model:dialog-open="languageDialogOpen" />

        <div class="logo">
          <a
            :href="homepageUrl"
            v-if="useExternalHeaderLayout"
            target="_blank"
            data-test="header-link-homepage"
          >
            <img class="logo__image" :src="logo" alt="EgeTrans" />
          </a>
          <router-link
            :to="{ name: RouteNames.DASHBOARD }"
            v-else
            data-test="header-link-etconnect"
          >
            <img
              class="logo__image"
              :src="logo"
              alt="ETConnect"
              :style="{
                width: Config.isCompass ? '170px' : '120px',
                'margin-right': Config.isCompass ? '-12px' : '0px',
              }"
            />
          </router-link>
        </div>
      </div>
    </div>
    <div v-if="!useExternalHeaderLayout && isMobile" class="header__navigation">
      <NavigationDropdownMobile />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import LanguageSelection from "@/components/header/LanguageSelection.vue";
import ExternalLinks from "@/components/header/ExternalLinks.vue";
import NavigationDropdown from "@/components/header/NavigationDropdown.vue";
import { RouteNames } from "@/router";
import { useAuthStore } from "@/stores/auth";
import { HomepageSubPage, useHomepageLink } from "@/composables/homepageLink";
import egetransLogoImg from "@/assets/egetrans-logo.svg?url";
import etconnectLogoImg from "@/assets/etconnect-logo.svg?url";
import etconnectMarkImg from "@/assets/etconnect-mark.svg?url";
import compassLogoImg from "@/assets/compass-logo.svg?url";
import compassIconImg from "@/assets/compass-icon.svg?url";
import Config from "@/common/config";
import NavigationDropdownMobile from "@/components/header/NavigationDropdownMobile.vue";
import { SVGIcon } from "et-components";
import { useNavigationState } from "@/components/header/composables/navigationState";

const { navigationOpen, setNavigationOpen } = useNavigationState();

const externalDialogOpen = ref(false);
const languageDialogOpen = ref(false);

const authStore = useAuthStore();

const isMobile = ref(false);

const updateIsMobile = () => {
  isMobile.value = window.innerWidth <= 1280;
};

onMounted(() => {
  updateIsMobile();
  window.addEventListener("resize", updateIsMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateIsMobile);
});

// whenever the user is not logged in or not activated (e.g. when registering), use an alternative header layout
const useExternalHeaderLayout = computed(
  () =>
    !(
      authStore.isSignedIn &&
      (authStore.isActivated || authStore.isImpersonationActive)
    ),
);

const { getHomepageLink } = useHomepageLink();
const homepageUrl = computed(() => getHomepageLink(HomepageSubPage.HOMEPAGE));
const logo = computed(() => {
  if (useExternalHeaderLayout.value) {
    return egetransLogoImg; //TODO: do we keep the EgeTrans-Homepage link?
  }
  return Config.isCompass ? compassLogoImg : etconnectLogoImg;
});
const icon = computed(() => {
  return Config.isCompass ? compassIconImg : etconnectMarkImg;
});
</script>

<style scoped lang="scss">
.header {
  background: white;
  box-shadow: 0 0.1rem 1rem 0 rgb(0 0 0 / 15%);
}

.header-content {
  display: flex;
  align-items: center;
  height: $layout-header-height;
}

.header__navigation {
  flex: 0 1 auto;
  min-width: 0;
}

.header__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: 100%;
  margin-left: auto;
}

.etconnect-mark {
  height: 24px;
}

.etconnect-mark-link {
  display: inline-block;
  line-height: 0;
}

.logo {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: 100%;
  padding-left: 1.6rem;
}

.logo__image {
  display: block;
  width: 130px;
  height: 100%;
}
</style>
